import React, { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Divider, Grid, Stack } from '@mui/material';
import useImages from 'api/useImages';
import { EAssetType, ESectionOfferType } from 'constants/enums';
import { useFormikContext } from 'formik';
import { UploadsTooltips } from 'hooks/useUpload';
import { useSectionsState } from 'state/hooks/sections.hook';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';
import ActionButton from 'components/ActionButton/ActionButton';

import { SectionsItem, StoreThemeFormValues } from '../storeTheme.types';

import { StoreThemeSectionsTable } from './SectionsTable';

export const Sections: React.FC = () => {
  const { values, handleBlur, setFieldValue, errors } =
    useFormikContext<StoreThemeFormValues>();

  const { publisherId } = useParams();

  const pictures = useImages(publisherId).getImages;
  const {
    editedSectionId,
    setEditedSectionId,
    currentSection,
    setCurrentSection,
    bundleSections,
    setBundleSections,
    promotionSections,
    setPromotionSections
  } = useSectionsState();
  const [currentIdError, setCurrentIdError] = useState<string>('');
  const [currentNameError, setCurrentNameError] = useState<string>('');

  const bundleSectionsTableItems: SectionsItem[] = useMemo(() => {
    if (bundleSections && bundleSections.length > 0) {
      return bundleSections.map((item, index) => ({
        ...item,
        _id: item.publisherSectionId || index.toString(),
        type: 'Section'
      }));
    }
    return [];
  }, [bundleSections]);

  const promotionSectionsTableItems: SectionsItem[] = useMemo(() => {
    if (promotionSections && promotionSections.length > 0) {
      return promotionSections.map((item, index) => ({
        ...item,
        _id: item.publisherSectionId || index.toString(),
        type: 'Section'
      }));
    }
    return [];
  }, [promotionSections]);

  useEffect(() => {
    if (values.bundleSections && values.bundleSections.length > 0) {
      setBundleSections(values.bundleSections);
    }
  }, [values.bundleSections]);

  useEffect(() => {
    if (values.promotionSections && values.promotionSections.length > 0) {
      setPromotionSections(values.promotionSections);
    }
  }, [values.promotionSections]);

  const handleChangeSectionsList = () => {
    return !!editedSectionId ? editSection() : addSection();
  };

  const addSection = () => {
    if (!currentSection) return;

    const sectionToAdd = {
      _id: currentSection.publisherSectionId,
      publisherSectionId: currentSection.publisherSectionId,
      name: currentSection.name,
      type: 'Section',
      image: currentSection?.image,
      offerType: currentSection?.offerType || ESectionOfferType.BUNDLE
    };
    if (sectionToAdd.offerType === ESectionOfferType.BUNDLE) {
      setBundleSections([...(bundleSections || []), sectionToAdd]);
    } else {
      setPromotionSections([...(promotionSections || []), sectionToAdd]);
    }

    const sectionFieldValue: SectionsItem = {
      _id: sectionToAdd.publisherSectionId || sectionToAdd.name,
      publisherSectionId: sectionToAdd.publisherSectionId,
      name: sectionToAdd.name,
      offerType: sectionToAdd.offerType,
      toAdd: true
    };
    if (sectionToAdd.image) {
      sectionFieldValue.image = sectionToAdd.image;
    }
    if (sectionToAdd.offerType === ESectionOfferType.BUNDLE) {
      setFieldValue('bundleSections', [
        ...(values.bundleSections || []),
        sectionFieldValue
      ]);
    } else {
      setFieldValue('promotionSections', [
        ...(values.promotionSections || []),
        sectionFieldValue
      ]);
    }
    setFieldValue('sectionId', '');
    setFieldValue('sectionName', '');
    setFieldValue('sectionOfferType', ESectionOfferType.BUNDLE);
    setCurrentSection(null);
  };

  const editSection = () => {
    if (!currentSection) return;

    let sections, setSections, setField;
    if (currentSection.offerType === ESectionOfferType.BUNDLE) {
      sections = bundleSections;
      setSections = setBundleSections;
      setField = (value: SectionsItem[]) =>
        setFieldValue('bundleSections', value);
    } else {
      sections = promotionSections;
      setSections = setPromotionSections;
      setField = (value: SectionsItem[]) =>
        setFieldValue('promotionSections', value);
    }
    const updatedSectionsList: SectionsItem[] | undefined = sections?.map(
      (section) => {
        if (section.publisherSectionId === editedSectionId) {
          const updatedSection: SectionsItem = {
            _id: currentSection.publisherSectionId,
            publisherSectionId: currentSection.publisherSectionId,
            sectionId: currentSection.sectionId,
            name: currentSection.name,
            offerType: currentSection.offerType || ESectionOfferType.BUNDLE
          };
          if (currentSection.image) {
            updatedSection.image = currentSection.image;
          }
          return updatedSection;
        }
        return section;
      }
    );
    setEditedSectionId('');
    setSections(updatedSectionsList || []);
    setField(updatedSectionsList || []);
    setFieldValue('sectionId', '');
    setFieldValue('sectionName', '');
    setFieldValue('sectionOfferType', ESectionOfferType.BUNDLE);
    setCurrentSection(null);
  };

  const getSectionImgItems = () => {
    if (!pictures.isLoading && pictures.data) {
      const filteredPic = (pictures.data || []).filter(
        (p: any) =>
          p.type === EAssetType.GENERAL || p.type === EAssetType.SECTION
      );
      return filteredPic.length > 0
        ? filteredPic.map((picture: any) => ({
            content: picture.name,
            key: picture.url,
            value: picture.url,
            renderFunction: () => (
              <Stack
                className="formContent-imageSelected"
                direction="row"
                spacing={2}
              >
                <img alt={picture.name} src={picture.url} />
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    marginLeft: 0
                  }}
                >
                  {picture.name}
                </div>
              </Stack>
            )
          }))
        : [];
    }

    return [];
  };

  useEffect(() => {
    if (
      currentSection?.name &&
      (values.bundleSections || [])
        .concat(values.promotionSections || [])
        .some(
          (item: SectionsItem) =>
            item.publisherSectionId !== editedSectionId &&
            item.name === currentSection.name
        )
    ) {
      setCurrentNameError('Section name already exists');
    } else {
      setCurrentNameError('');
    }
  }, [currentSection?.name]);

  useEffect(() => {
    if (
      currentSection?.publisherSectionId &&
      currentSection.publisherSectionId !== editedSectionId &&
      (values.bundleSections || [])
        .concat(values.promotionSections || [])
        .some(
          (item: SectionsItem) =>
            item.publisherSectionId === currentSection.publisherSectionId
        )
    ) {
      setCurrentIdError('Section ID already exists');
    } else if (
      currentSection?.publisherSectionId &&
      currentSection.publisherSectionId.includes(' ')
    ) {
      setCurrentIdError('Section ID should not contain spaces');
    } else {
      setCurrentIdError('');
    }
  }, [currentSection?.publisherSectionId]);

  return (
    <>
      <AcCard stackContainer={false} title="Sections">
        <Grid container rowSpacing={1.5} columnSpacing={1.5}>
          <Grid item xs={6}>
            <AcInput
              header="Section name"
              name="sectionName"
              value={currentSection?.name || ''}
              tooltip="This will be the name of your section"
              type="text"
              dataTestId="sectionName"
              onChange={(e) => {
                setCurrentSection({
                  ...currentSection,
                  publisherSectionId: currentSection?.publisherSectionId || '',
                  name: (e.target.value as string) || ''
                });
              }}
              onBlur={handleBlur}
              error={currentNameError !== ''}
              helperText={currentNameError}
            />
          </Grid>

          <Grid item xs={6}>
            <AcInput
              header="ID"
              name="sectionId"
              value={currentSection?.publisherSectionId || ''}
              type="text"
              dataTestId="sectionId"
              onChange={(e) => {
                setCurrentSection({
                  ...currentSection,
                  publisherSectionId: (e.target.value as string) || '',
                  name: currentSection?.name || ''
                });
              }}
              onBlur={handleBlur}
              error={currentIdError !== ''}
              helperText={currentIdError}
            />
          </Grid>

          <Grid item xs={6}>
            <AcSelect
              header="Image"
              name="sectionImage"
              value={currentSection?.image || ''}
              dataTestBoxId="sectionImage"
              uploadBtnTestId="uploadSectionImage"
              dataTestListId="sectionImageList"
              optionDataTestIdPrefix="sectionImageOption"
              onChange={(e) => {
                setCurrentSection({
                  ...currentSection,
                  name: currentSection?.name || '',
                  image: e.target.value as string,
                  publisherSectionId: currentSection?.publisherSectionId || ''
                });
              }}
              onBlur={handleBlur}
              onClear={() => {
                if (currentSection) {
                  const { image, ...rest } = currentSection;
                  setCurrentSection(rest);
                }
              }}
              disabled={pictures.isLoading || pictures.isRefetching}
              uploadConfig={{
                onUploadSuccess: async (url: string) => {
                  await pictures.refetch();
                  setCurrentSection({
                    ...currentSection,
                    name: currentSection?.name || '',
                    image: url,
                    publisherSectionId: currentSection?.publisherSectionId || ''
                  });
                },
                uploadType: EAssetType.SECTION,
                uploadMessage: UploadsTooltips[EAssetType.SECTION]
              }}
              items={getSectionImgItems()}
              tooltip={
                'For optimized display, make sure your image are at least 300px with 1:1 aspect ratio'
              }
            />
          </Grid>

          <Grid item xs={6}>
            <AcSelect
              placeholder="Select position"
              header="Type"
              name="sectionOfferType"
              value={currentSection?.offerType || ESectionOfferType.BUNDLE}
              dataTestBoxId="sectionOfferType"
              onChange={(e) => {
                setCurrentSection({
                  ...currentSection,
                  name: currentSection?.name || '',
                  publisherSectionId: currentSection?.publisherSectionId || '',
                  offerType: e.target.value as ESectionOfferType
                });
              }}
              disabled={!!editedSectionId}
              onBlur={handleBlur}
              items={[
                {
                  content: 'Bundle',
                  key: ESectionOfferType.BUNDLE,
                  value: ESectionOfferType.BUNDLE
                },
                {
                  content: 'Promotion',
                  key: ESectionOfferType.PROMOTION,
                  value: ESectionOfferType.PROMOTION
                }
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <ActionButton
              text={!!editedSectionId ? 'Save' : 'Add'}
              variant="outlined"
              dataTestId="sectionAddButton"
              disabled={
                !currentSection?.publisherSectionId ||
                !currentSection?.name ||
                Boolean(errors?.sectionId) ||
                Boolean(errors?.sectionName) ||
                currentIdError !== '' ||
                currentNameError !== ''
              }
              onClick={handleChangeSectionsList}
            />
          </Grid>
        </Grid>
      </AcCard>

      {bundleSections && bundleSections.length > 0 && (
        <>
          <Divider />

          <AcCard stackContainer={false} title="Bundles">
            <Grid container rowSpacing={1.5} columnSpacing={1.5}>
              <Grid item xs={12} mt={-3}>
                <StoreThemeSectionsTable
                  sections={bundleSectionsTableItems}
                  setSections={setBundleSections}
                  setFieldValue={setFieldValue}
                />
              </Grid>
            </Grid>
          </AcCard>
        </>
      )}

      {promotionSections && promotionSections.length > 0 && (
        <>
          <Divider />

          <AcCard stackContainer={false} title="Promotions">
            <Grid container rowSpacing={1.5} columnSpacing={1.5}>
              <Grid item xs={12} mt={-3}>
                <StoreThemeSectionsTable
                  sections={promotionSectionsTableItems}
                  setSections={setPromotionSections}
                  setFieldValue={setFieldValue}
                />
              </Grid>
            </Grid>
          </AcCard>
        </>
      )}
    </>
  );
};
